/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { AxiosResponse } from "axios";
import {
    Effect,
    Nullable,
    Setter,
    ServiceLocation,
    Mapping,
    WorkOrderStatus,
    User,
    StorageLocationDetailed,
    FlattenedInventoryItem,
    InventoryItem,
    StorageLocation,
    TechnicianEntryEgressInventory,
} from "./Interfaces";
import { StateMapping, STATES_MAPPING } from "./Constants";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { getHandler, stdErrHandlerFactory } from "./Requests";

export enum S3PDFRoute {
    TechnicianEntry = "/api/tech-entries/generate-form-pdf/",
    WorkOrder = "/api/tech-entries/generate-consolidated-form-pdf/",
    StorageLocationAudit = "/api/locations/storage-locations/print-audit/",
}

/**
 * Utility function for handling Technician Entry and Work Order downloads.
 *
 * @param pdfType Type of PDF based on the defined Enums
 * @param objectId General ID of the object.
 * @param dispatch App dispatch function
 * @param setProcessing Callback for providing status updates.
 * @param targetFileName Name of the file when it's downloaded to the system.
 */
export function handleS3PDFDownload(
    pdfType: S3PDFRoute,
    objectId: number,
    dispatch: Dispatch<AnyAction>,
    setProcessing: Setter<boolean>,
    targetFileName: string
) {
    let route = `${pdfType}${objectId}`;

    setProcessing(true);

    getHandler<{ document_url: string }>(
        route,
        dispatch,
        (response: AxiosResponse<{ document_url: string }, any>) => {
            fetch(response.data.document_url, { method: "GET" })
                .then((response) => response.blob())
                .then((blob) => {
                    setProcessing(false);

                    // Create blob link to download
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", targetFileName);

                    // Append to html link element page
                    document.body.appendChild(link);

                    // Start download
                    link.click();

                    // Clean up and remove the link
                    link.parentNode?.removeChild(link);
                })
                .catch(() => {
                    setProcessing(false);
                });
        },
        stdErrHandlerFactory(dispatch, () => setProcessing(false))
    );
}

export function zipCodeString(site: ServiceLocation): string {
    if (site === undefined || site === null) {
        return "";
    }

    if (site.zip_code === undefined || site.zip_code === null) {
        return "";
    }

    return `${site.zip_code}`;
}

/**
 * This function takes in a standard work site and generates a URL linking to Google Maps.
 * @param site
 * @param travelMode
 */
export function getGoogleMapsURL(site: ServiceLocation, travelMode: string = "driving"): string {
    if (site === undefined || site === null) {
        return "#";
    }
    if (site.street_address === undefined || site.street_address === null) {
        return "#";
    }
    if (site.city === undefined || site.city === null) {
        return "#";
    }
    if (site.state === undefined || site.state === null) {
        return "#";
    }

    let address = site.street_address.replaceAll(" ", "+") + "+" + site.city.replaceAll(" ", "+") + "+" + site.state;

    if (site.zip_code !== undefined && site.zip_code !== null) {
        address += "+" + site.zip_code;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=${travelMode}`;
}

/**
 * TODO test and update this function.
 * @param datetimeRaw
 */
export function convertHeaderDateTimes(datetimeRaw: Nullable<string | Date>): Nullable<string> {
    if (!datetimeRaw) {
        return null;
    }

    let datetime: Date = new Date(datetimeRaw);

    const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    const parts = formatter.formatToParts(datetime);
    const getValue = (type: string) => parts.find((p) => p.type === type)?.value;

    return `${getValue("year")}-${getValue("month")}-${getValue("day")}T${getValue("hour")}:${getValue("minute")}`;
}

export function getStateMapping(stateName: string): StateMapping {
    let statesFromAbbrev: StateMapping[] = STATES_MAPPING.filter(
        (state) => state.abbrev.toLowerCase().trim() === stateName.toLowerCase().trim()
    );

    if (statesFromAbbrev.length > 0) {
        return statesFromAbbrev[0];
    }

    let statesFromName: StateMapping[] = STATES_MAPPING.filter(
        (state) => state.name.toLowerCase().trim() === stateName.toLowerCase().trim()
    );

    if (statesFromName.length > 0) {
        return statesFromName[0];
    }

    return { name: "Virginia", abbrev: "VA" };
}

export function usingDarkTheme(): boolean {
    return localStorage.getItem("techwiseTheme") === "dark";
}

export function customScrollbarStyle() {
    let darkTheme: boolean = usingDarkTheme();

    let thumbColor: string = darkTheme ? "#666666" : (process.env.REACT_APP_PRIMARY_COLOR_LIGHT as string);

    let trackColor: string = darkTheme ? "#BBBBBB" : "#AFAFAF";

    return {
        "::-webkit-scrollbar": {
            width: "16px",
        },
        "::-webkit-scrollbar-track": {
            background: trackColor,
            borderRadius: 2,
        },
        "::-webkit-scrollbar-thumb": {
            backgroundColor: thumbColor,
            borderRadius: 2,
        },
    };
}

export function customDataGridScrollbarStyle() {
    let darkTheme: boolean = usingDarkTheme();

    let thumbColor: string = darkTheme ? "#666666" : (process.env.REACT_APP_PRIMARY_COLOR_LIGHT as string);

    let trackColor: string = darkTheme ? "#BBBBBB" : "#AFAFAF";

    return {
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "16px",
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: trackColor,
            borderRadius: 2,
        },
        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: thumbColor,
            borderRadius: 2,
        },
    };
}

export function formatDateWorkOrderHeader(date: string | Date) {
    if (date === null || date === undefined || date === "") {
        return "--";
    }

    let d = new Date(typeof date === "string" ? new Date(date) : date);

    let mins: number = d.getMinutes();
    let hrs: number = d.getHours();
    let minsFormat: string = mins.toString();

    if (mins < 10) {
        minsFormat = `0${mins}`;
    }

    let ampm: string = "AM";

    if (hrs > 12) {
        hrs -= 12;
        ampm = "PM";
    } else if (hrs === 12) {
        ampm = "PM";
    } else if (hrs === 0) {
        hrs = 12;
    }

    return `${d.toLocaleDateString()} ${hrs}:${minsFormat} ${ampm}`;
}

export function mapWorkOrderStatusString(workOrderStatus: WorkOrderStatus) {
    const statusDict = {
        UN: "UNASSIGNED",
        IP: "IN PROGRESS",
        SB: "SUBMITTED",
        CM: "COMPLETED",
        AR: "ARCHIVE",
    };

    return statusDict[workOrderStatus as keyof typeof statusDict];
}

export function sanitizeFileName(fileName: string, defaultValue: string = "file.pdf"): string {
    let sanitized = fileName
        .replaceAll("/", "")
        .replaceAll("\\", "")
        .replaceAll('"', "")
        .replaceAll("*", "")
        .replaceAll("?", "")
        .replaceAll("<", "")
        .replaceAll(">", "");

    if (!sanitized.trim()) {
        return defaultValue;
    } else {
        return sanitized;
    }
}

/**
 * Capitalizes the first character of all words (space delimited) in a provided string.
 * @param text Text to capitalize.
 */
export function title(text: string): string {
    return text.replace(/\w\S*/g, function (_text) {
        return _text.charAt(0).toUpperCase() + _text.substr(1).toLowerCase();
    });
}

/**
 * Executes a function after a delay.
 * @param delay Delay in milliseconds.
 * @param action Action to execute.
 */
export async function delayedAction(delay: number, action: Effect) {
    await new Promise((r) => setTimeout(r, delay));
    action();
}

export function filterCollection<T, V = any>(
    fullCollection: T[],
    subset: T[],
    mapping: Mapping<T, V> = (value: T) => value as any
): T[] {
    let subsetMapping: V[] = subset.map((elem: T) => mapping(elem));
    return fullCollection.filter((elem: T) => !subsetMapping.includes(mapping(elem)));
}

export function formatDataSize(size: number) {
    if (size >= 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
    if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (size >= 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    }
    return `${size} B`;
}

export function removeArrayElem(array: any[], index: number) {
    array.splice(index, 1);
}

export function validateEmail(email: string): boolean {
    let emailMatch = email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );

    return emailMatch !== null;
}

export function validateUsername(username: string): boolean {
    return !username.trim().includes(" ");
}

export function sortUsersByName(users: User[]): User[] {
    return [...users].sort((user1: User, user2: User) =>
        (user1.first_name + user1.last_name).toLowerCase() > (user2.first_name + user2.last_name).toLowerCase() ? 1 : -1
    );
}

export function getInventoryCompilation(storageLocations: StorageLocationDetailed[]): FlattenedInventoryItem[] {
    let flattened: FlattenedInventoryItem[] = [];

    storageLocations.map((storageLocation: StorageLocationDetailed) => {
        storageLocation.inventory.map((inventory: InventoryItem) => {
            let storageLocationSimple: StorageLocation = {
                id: storageLocation.id,
                location_type: storageLocation.location_type,
                parent: storageLocation.parent,
                name: storageLocation.name,
                responsible_user: storageLocation.responsible_user,
            };

            let flattenedInventory: FlattenedInventoryItem = { ...inventory };

            flattenedInventory.storage_location = storageLocationSimple;

            flattened.push(flattenedInventory);

            return null;
        });
        return null;
    });

    return flattened;
}

export function listStorageLocationsFromInventories(inventories: InventoryItem[]): StorageLocation[] {
    let storageLocations: StorageLocation[] = [];
    let mappedStorageLocationIds: number[] = [];

    inventories.map((inventory: InventoryItem) => {
        if (!mappedStorageLocationIds.includes(inventory.storage_location.id)) {
            storageLocations.push(inventory.storage_location);
            mappedStorageLocationIds.push(inventory.storage_location.id);
        }
        return null;
    });

    return storageLocations;
}

export function getInventoriesInStorageLocation(
    storageLocation: Nullable<StorageLocation>,
    existingEgresses: TechnicianEntryEgressInventory[],
    allInventories: InventoryItem[]
): InventoryItem[] {
    const existingInventories: number[] = existingEgresses.map(
        (technicianEntryEgress: TechnicianEntryEgressInventory) => technicianEntryEgress.inventory.id
    );
    return allInventories.filter(
        (inventory: InventoryItem) =>
            (inventory.storage_location.id === storageLocation?.id ?? 0) && !existingInventories.includes(inventory.id)
    );
}

export function isNumeric(str: any): boolean {
    if (typeof str !== "string") {
        return false;
    }

    // @ts-ignore
    return !isNaN(str) && !isNaN(parseFloat(str));
}

/**
 * Generates a UUID without requiring a dependency on the Node Crypto or Stream libraries.
 * NOTE: This is only useful for local IDs where cryptographic security is not a requirement.
 */
export function generateUuidNative(): string {
    let d = new Date().getTime(); // Get current timestamp
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (d + Math.random() * 16) % 16 | 0; // Generate random hex digit
        d = Math.floor(d / 16); // Update d for the next iteration
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16); // Format and convert to hex
    });
}

/**
 * Naive implementation to convert a local time to its UTC equivalent.
 * @param localTime The local datetime expressed with a timezone.
 */
export function toUTC(localTime: Date): Date {
    const dateUtc: number = Date.UTC(
        localTime.getUTCFullYear(),
        localTime.getUTCMonth(),
        localTime.getUTCDate(),
        localTime.getUTCHours(),
        localTime.getUTCMinutes(),
        localTime.getUTCSeconds()
    );
    return new Date(dateUtc);
}

export function chatDateStylization(dateTimestamp: string) {
    const date: Date = new Date(dateTimestamp);

    let dateString = date.toLocaleDateString();

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.getDate() === yesterday.getDate()) {
        dateString = "Yesterday";
    }

    if (date.getDate() === today.getDate()) {
        dateString = "Today";
    }

    return `${dateString} · ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

/**
 * Creates a hash for a provided string. Implementation similar to Object.hashCode() in Java.
 * @param str string to convert.
 */
function hashCode(str: string): number {
    let hash = 0;
    if (str.length === 0) {
        return hash;
    }
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0; // Ensure 32-bit integer
    }
    return hash;
}

/**
 * Converts a string into an RGB color (16.7M / 24-bit) using a hashcode. Result is returned as a CSS rgba function.
 * @param str Input string to colorize.
 * @param alpha Transparency of the returned result.
 */
export function generateColorFromString(str: string, alpha: number = 1.0): string {
    const baseCode: number = hashCode(str);
    const trimmedCode: number = baseCode & 0x00ffffff;
    const red: number = trimmedCode >> 16;
    const green: number = (trimmedCode & 0x0000ffff) >> 8;
    const blue: number = trimmedCode & 0x000000ff;

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function computeAcquisitionPrice(unitPrice: number, salesTax: number, shippingPrice: number): number {
    return unitPrice + salesTax + shippingPrice;
}

export function computeRetailPrice(unitPrice: number, salesTax: number, shippingPrice: number, markup: number): number {
    return unitPrice + salesTax + shippingPrice + (unitPrice + shippingPrice) * (markup / 100.0);
}

export function computeProfit(unitPrice: number, shippingPrice: number, markup: number): number {
    return (unitPrice + shippingPrice) * (markup / 100.0);
}
